import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import t from '../translations';

function SkeletonCard() {
  return (
    <div style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, padding: '16px', marginBottom: 10 }}>
      <div className="skeleton" style={{ height: 12, width: 80, marginBottom: 10 }} />
      <div className="skeleton" style={{ height: 16, width: '60%', marginBottom: 8 }} />
      <div className="skeleton" style={{ height: 12, width: '90%' }} />
    </div>
  );
}

export default function HistoryPage() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const tr = t[lang];

  const [readings, setReadings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');

  useEffect(() => {
    api.get('/api/reading/history')
      .then(d => { setReadings(d.readings || []); setLoading(false); })
      .catch(err => {
        if (err.status === 402) navigate('/paywall');
        else { setError('Could not load history.'); setLoading(false); }
      });
  }, [navigate]);

  const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div style={{ minHeight: '100vh', background: '#0A0F1E', paddingBottom: 48 }}>

      {/* Header */}
      <div style={{ padding: '56px 20px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={() => navigate('/home')}
          style={{ background: 'none', border: 'none', color: '#C9A84C', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}
        >
          <ChevronLeft size={22} />
        </button>
        <div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: '#F5F0E8', fontWeight: 700 }}>
            {tr.readingHistory}
          </h1>
          <p style={{ fontSize: 13, color: '#8A8580', marginTop: 2 }}>{tr.readingHistorySubtitle}</p>
        </div>
      </div>

      <div style={{ padding: '0 20px' }}>
        {loading && [0,1,2,3].map(i => <SkeletonCard key={i} />)}

        {error && <p style={{ color: '#C0392B', fontSize: 13, textAlign: 'center', padding: 24 }}>{error}</p>}

        {!loading && readings.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <p style={{ fontSize: 32, marginBottom: 12, color: '#C9A84C' }}>✦</p>
            <p style={{ fontSize: 15, color: '#8A8580' }}>No readings yet. Reveal your first reading!</p>
            <button className="btn-primary" onClick={() => navigate('/home')} style={{ marginTop: 20, maxWidth: 240 }}>
              Go to Home
            </button>
          </div>
        )}

        {!loading && readings.map((r, i) => {
          const d = new Date(r.date + 'T00:00:00');
          const dayName = DAY_NAMES[d.getDay()];
          const dateStr = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
          const color   = r.colorReading;
          const isToday = r.date === new Date().toISOString().split('T')[0];
          return (
            <div
              key={r.date}
              data-testid={`history-card-${r.date}`}
              onClick={() => navigate('/reading', { state: { historyDate: r.date } })}
              style={{
                background: '#111827',
                border: `1px solid ${isToday ? 'rgba(201,168,76,0.35)' : 'rgba(255,255,255,0.06)'}`,
                borderRadius: 14, padding: '16px',
                marginBottom: 10, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 14,
                animation: `fadeInUp 0.3s ease ${i * 0.06}s forwards`,
                opacity: 0,
              }}
            >
              {/* Date pill */}
              <div style={{
                width: 48, height: 56, borderRadius: 12, flexShrink: 0,
                background: isToday ? 'rgba(201,168,76,0.12)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${isToday ? 'rgba(201,168,76,0.3)' : 'rgba(255,255,255,0.08)'}`,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              }}>
                <p style={{ fontSize: 9, color: '#8A8580', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{dayName}</p>
                <p style={{ fontSize: 18, fontWeight: 700, color: isToday ? '#C9A84C' : '#F5F0E8', fontFamily: "'Playfair Display', serif", lineHeight: 1 }}>
                  {dateStr.split(' ')[0]}
                </p>
                <p style={{ fontSize: 9, color: '#8A8580', marginTop: 2 }}>{dateStr.split(' ')[1]}</p>
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                {color && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: color.hexCode || '#C9A84C', flexShrink: 0,
                    }} />
                    <p style={{ fontSize: 12, fontWeight: 600, color: '#C9A84C' }}>{color.name}</p>
                  </div>
                )}
                <p style={{
                  fontSize: 13, color: '#F5F0E8', lineHeight: 1.45,
                  overflow: 'hidden', display: '-webkit-box',
                  WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                }}>
                  {r.jyotisNote || 'Tap to view reading'}
                </p>
              </div>

              <ChevronRight size={16} color="#8A8580" style={{ flexShrink: 0 }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
